import React, { useRef, useEffect, useState, useCallback } from 'react';

// ── Flip-word data ────────────────────────────────────────────────────────────
const videos = [
  {
    src: '/vids/cvui.mp4',
    poster: '/vids/cvui-poster.jpg',
    title: 'Computer Vision AR UI',
    descBefore: 'Design AR interfaces in a',
    flipWords: ['spatial', 'vision-powered', 'immersive'],
    descAfter: 'CV environment',
    githubUrl: 'https://github.com/Seth141/Computer-Vision-AR-UI',
  },
  {
    src: '/vids/converse.mp4',
    poster: '/vids/converse-poster.jpg',
    title: 'Converse',
    descBefore: 'AI conversation enhancement,',
    flipWords: ['live', 'seamless', 'real-time'],
    descAfter: 'in your AR glasses',
    githubUrl: 'https://github.com/Seth141/Converse_Augmented',
  },
  {
    src: '/vids/inkair.mp4',
    poster: '/vids/inkair-poster.jpg',
    title: 'InkAir',
    descBefore: 'Your AR e-reader with an',
    flipWords: ['intelligent', 'AI-powered', 'personal'],
    descAfter: 'librarian',
    githubUrl: 'https://github.com/Seth141/InkAir',
  },
];

// ── FlipWord ──────────────────────────────────────────────────────────────────
const FlipWord = ({ words }) => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('idle');

  useEffect(() => {
    const timeouts = [];
    const id = setInterval(() => {
      setPhase('out');
      timeouts.push(setTimeout(() => {
        setIndex(i => (i + 1) % words.length);
        setPhase('in');
        timeouts.push(setTimeout(() => setPhase('idle'), 320));
      }, 280));
    }, 5000);
    return () => {
      clearInterval(id);
      timeouts.forEach(clearTimeout);
    };
  }, [words.length]);

  return (
    <span className={`vs-flip-word vs-flip-word--${phase}`}>
      {words[index]}
    </span>
  );
};

// ── Play/Pause SVG icons ──────────────────────────────────────────────────────
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="vs-ctrl-icon">
    <path d="M8 5.14v14l11-7-11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="vs-ctrl-icon">
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

// ── GitHub icon SVG ───────────────────────────────────────────────────────────
const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="vs-gh-icon" aria-hidden="true">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.605-3.369-1.342-3.369-1.342-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.607.069-.607 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.087.636-1.337-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
  </svg>
);

// ── VideoCard ─────────────────────────────────────────────────────────────────
const VideoCard = ({ src, poster, title, descBefore, flipWords, descAfter, githubUrl, shineDelay }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // IntersectionObserver: autoplay (muted) when scrolled into view on every
  // platform, including mobile. Reduced-motion users are still respected.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const canAmbientPlay =
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const tryPlay = (vid) => {
      // First attempt — succeeds on desktop and most mobile cases.
      const p = vid.play();
      if (p && typeof p.then === 'function') {
        p.then(() => setIsPlaying(true)).catch(() => {
          // On mobile, the very first play() right after load() can fail
          // because the video isn't yet decode-ready. Retry once when the
          // browser signals it can play.
          const onCanPlay = () => {
            vid.removeEventListener('canplay', onCanPlay);
            vid.play().then(() => setIsPlaying(true)).catch(() => {});
          };
          vid.addEventListener('canplay', onCanPlay, { once: true });
        });
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        const vid = videoRef.current;
        if (!vid) return;
        if (entry.isIntersecting && canAmbientPlay) {
          // playsInline + muted are already on the <video>; ensure
          // muted is sticky (some mobile browsers reset it on .load()).
          vid.muted = true;
          if (!loaded) { vid.load(); setLoaded(true); }
          tryPlay(vid);
        } else {
          vid.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.3, rootMargin: '120px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loaded]);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    if (isPlaying) {
      vid.pause();
      setIsPlaying(false);
    } else {
      if (!loaded) { vid.load(); setLoaded(true); }
      vid.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying, loaded]);

  // Keep isPlaying in sync if the browser pauses/resumes on its own
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    vid.addEventListener('play', onPlay);
    vid.addEventListener('pause', onPause);
    return () => { vid.removeEventListener('play', onPlay); vid.removeEventListener('pause', onPause); };
  }, []);

  const showOverlay = !isPlaying || hovered;

  return (
    <div
      ref={containerRef}
      className="vs-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
        {/* ── Video media ── */}
        <div className="vs-media" onClick={togglePlay}>
          <video
            ref={videoRef}
            className="vs-video"
            poster={poster}
            muted
            loop
            playsInline
            preload="none"
          >
            <source src={src} type="video/mp4" />
          </video>

          {/* Overlay — always shown when paused, shown on hover when playing */}
          <div className={`vs-overlay ${showOverlay ? 'vs-overlay--visible' : ''}`}>
            <button className={`vs-ctrl-btn ${isPlaying ? 'vs-ctrl-btn--pause' : 'vs-ctrl-btn--play'}`} aria-label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
          </div>

          {/* Top-edge inner shine */}
          <div className="vs-inner-shine" />
        </div>

        {/* ── Description ── */}
        <div className="vs-body">
          <h3 className="vs-title">{title}</h3>
          <p className="vs-desc">
            {descBefore}&nbsp;
            <FlipWord words={flipWords} />
            &nbsp;{descAfter}
          </p>
          <a
            href={githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="vs-github-btn"
            style={{ '--shine-delay': shineDelay }}
          >
            <GitHubIcon />
            View on GitHub
          </a>
        </div>
    </div>
  );
};

// ── VideoShowcase ─────────────────────────────────────────────────────────────
const VideoShowcase = () => (
  <div className="vs-section">
    <div className="vs-section-label">
      <span className="vs-label-line" />
      <span className="vs-label-text"> </span>
      <span className="vs-label-line" />
    </div>
    <div className="vs-grid">
      {videos.map((v, i) => <VideoCard key={v.src} {...v} shineDelay={`${i * 1.4}s`} />)}
    </div>
  </div>
);

export default VideoShowcase;
